// Every "today" and every sheet date is read in Melbourne time, whatever the device clock says.
export const TZ = 'Australia/Melbourne';

const isoFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

/** Melbourne calendar date as YYYY-MM-DD. */
export function melbourneToday(now: Date = new Date()): string {
  return isoFmt.format(now);
}

/** Whole days from `today` to the ISO date `s` (negative if past), or null if blank. */
export function daysUntil(s: string | null | undefined, today: string): number | null {
  if (!s) return null;
  return Math.round((Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10)) - Date.UTC(+today.slice(0, 4), +today.slice(5, 7) - 1, +today.slice(8, 10))) / 864e5);
}

/** Melbourne calendar day of a date or timestamptz string. */
export function melbourneDay(s: string): string {
  return s.length > 10 ? isoFmt.format(new Date(s)) : s;
}

/** Card date: "3 Nov 26". Accepts a date or a timestamptz. */
export function fmtDate(s: string | null | undefined): string {
  if (!s) return '';
  return new Date(melbourneDay(s) + 'T00:00:00Z').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'UTC' });
}

/** Table date: "03/11/2026". Accepts a date or a timestamptz. */
export function fmtDMY(s: string | null | undefined): string {
  if (!s) return '';
  const d = melbourneDay(s);
  return d.slice(8, 10) + '/' + d.slice(5, 7) + '/' + d.slice(0, 4);
}

/** Weekday name + date for the calendar agenda: "Mon 3 Nov". */
export function fmtShortDay(s: string): string {
  return new Date(s + 'T00:00:00Z').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** "Last updated" stamp, shown in Melbourne time. */
export function fmtStamp(ts: string | null | undefined): string {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: TZ });
}

export function isoFromParts(y: number, m: number, d: number): string {
  return y + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0');
}

/** Add whole days to an ISO date. */
export function addDays(s: string, n: number): string {
  const d = new Date(s + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** 0 = Sunday ... 6 = Saturday, for an ISO date. */
export function weekday(s: string): number {
  return new Date(s + 'T00:00:00Z').getUTCDay();
}

const partsFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

/** Offset of Melbourne from UTC, in minutes, from the time zone database (slow: ~0.1 ms a call). */
function intlOffsetMinutes(utcMs: number): number {
  const p = Object.fromEntries(partsFmt.formatToParts(new Date(utcMs)).map((x) => [x.type, x.value]));
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return Math.round((asUtc - utcMs) / 60000);
}

const firstSundayUtc = (y: number, month0: number): number => {
  const dow = new Date(Date.UTC(y, month0, 1)).getUTCDay();
  return Date.UTC(y, month0, 1 + ((7 - dow) % 7));
};

/**
 * Offset of Melbourne from UTC, in minutes, at a given instant.
 * Victoria's rule since 2008: AEDT (+11) from 2am AEST on the first Sunday in October to 3am AEDT on the
 * first Sunday in April; both changes fall at 16:00 UTC on the Saturday. Plain arithmetic, because the sync
 * converts thousands of sheet timestamps within the Worker's CPU limit. Tests check it against Intl.
 */
export function tzOffsetMinutes(utcMs: number): number {
  const y = new Date(utcMs).getUTCFullYear();
  if (y < 2008 || y > 2099) return intlOffsetMinutes(utcMs);
  const dstEnds = firstSundayUtc(y, 3) - 8 * 3600e3;
  const dstStarts = firstSundayUtc(y, 9) - 8 * 3600e3;
  return utcMs < dstEnds || utcMs >= dstStarts ? 660 : 600;
}

/** Exposed for the test that keeps the fast rule honest. */
export const _intlOffsetMinutes = intlOffsetMinutes;

/** Melbourne wall-clock time → UTC ISO string. */
export function melbourneToUtcIso(y: number, mo: number, d: number, h = 0, mi = 0, s = 0): string {
  const wall = Date.UTC(y, mo - 1, d, h, mi, s);
  let utc = wall - tzOffsetMinutes(wall) * 60000;
  utc = wall - tzOffsetMinutes(utc) * 60000; // second pass settles DST edges
  return new Date(utc).toISOString();
}

const DMY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;

function validDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

/** Sheet "DD/MM/YYYY" → "YYYY-MM-DD". Blank → null. Anything else → undefined (unparseable). */
export function parseSheetDate(raw: string | null | undefined): string | null | undefined {
  const s = (raw ?? '').trim();
  if (!s) return null;
  const m = DMY.exec(s);
  if (!m) return undefined;
  const [y, mo, d] = [+m[3], +m[2], +m[1]];
  return validDate(y, mo, d) ? isoFromParts(y, mo, d) : undefined;
}

/** Sheet "DD/MM/YYYY HH:MM:SS" (Melbourne) → UTC ISO timestamp. Blank → null, unparseable → undefined. */
export function parseSheetTimestamp(raw: string | null | undefined): string | null | undefined {
  const s = (raw ?? '').trim();
  if (!s) return null;
  const m = DMY.exec(s);
  if (!m) return undefined;
  const [y, mo, d] = [+m[3], +m[2], +m[1]];
  if (!validDate(y, mo, d)) return undefined;
  const h = m[4] ? +m[4] : 0, mi = m[5] ? +m[5] : 0, se = m[6] ? +m[6] : 0;
  if (h > 23 || mi > 59 || se > 59) return undefined;
  return melbourneToUtcIso(y, mo, d, h, mi, se);
}
